import { useState } from 'react';

import { isNil } from 'lodash-es';
import { Navigate, useNavigate } from 'react-router';
import { useSearchParams } from 'react-router-dom';

import { useAuth0 } from '@auth0/auth0-react';
import { Link, Typography } from '@mui/material';

import AccessDenied from '@/components/shared/AccessDenied';
import { AccessDeniedPropType } from '@/components/shared/AccessDenied/AccessDenied';
import InkModal from '@/components/shared/InkModal/InkModal';
import { email } from '@/config';
import {
  OrganizationUserQuery,
  useAccountUserInvitationAcceptMutation,
  useAccountUserInvitationDeclineMutation,
} from '@/generated';
import {
  AccessTypeEnum,
  useSession,
} from '@/providers/SessionProvider/SessionProvider';
import { CURRENT_SESSION_QUERY } from '@/providers/SessionProvider/queries';
import { useSnackbar } from '@/providers/SnackbarProvider/SnackbarProvider';
import { Nullable } from '@/utils/globalTypes';
import { useDerivedValue } from '@/utils/useDerivedValue';

import { INVITATION_FAILURE_MESSAGE } from './constants';

const AccessRequired = () => {
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);

  const {
    accessType: sessionAccessType,
    currentUserAccounts,
    clients,
  } = useSession();
  const snackbar = useSnackbar();
  const navigate = useNavigate();
  const { logout } = useAuth0();
  const [searchParams] = useSearchParams();

  //Show invite message for the first account with invited status
  const {
    id: accountId,
    name: accountDisplayName,
    invitedByName,
  } = [...currentUserAccounts.values()].find(
    ({ status }) => status === 'INVITED',
  ) ?? {};

  const [acceptInvitation] = useAccountUserInvitationAcceptMutation({
    refetchQueries: [{ query: CURRENT_SESSION_QUERY }],
  });
  const [declineInvitation, { loading: isDecliningInvitation }] =
    useAccountUserInvitationDeclineMutation();

  const acceptAccountInvite = () => {
    if (!accountId) return;

    acceptInvitation({
      variables: { accountId },
      onCompleted: (data) => {
        if (!data.accountUserInvitationAccept2.success) {
          snackbar({
            message: INVITATION_FAILURE_MESSAGE,
            severity: 'error',
          });
        }
      },
      onError: (e) => {
        snackbar({
          message: e.message,
          severity: 'error',
        });
      },
      update: (cache, { data }) => {
        const { success, user } = data?.accountUserInvitationAccept2 ?? {};

        if (!success || !user) return;

        cache.modify<OrganizationUserQuery['organizationUser2']>({
          id: cache.identify(user),
          fields: {
            accountMembers: () => user.accountMembers,
          },
        });
      },
    });
  };
  const declineAccountInvite = () => {
    if (!accountId) return;

    const encodedAccountName = btoa(accountDisplayName!);

    declineInvitation({
      variables: { accountId },
      onCompleted: (data) => {
        if (data.accountUserInvitationDecline.success) {
          navigate(
            `/access/declined/${accountId}?account=${encodedAccountName}`,
          );
          return;
        }

        snackbar({
          message: INVITATION_FAILURE_MESSAGE,
          severity: 'error',
        });
      },
      onError: (e) => {
        snackbar({
          message: e.message,
          severity: 'error',
        });
      },
    });
  };

  const queryAccessType: Nullable<AccessTypeEnum> = searchParams.get('type')
    ? Number(searchParams.get('type'))
    : undefined;
  const accessType = useDerivedValue(() => {
    if (isNil(queryAccessType)) return sessionAccessType;

    if (
      queryAccessType !== AccessTypeEnum.Access_Denied &&
      queryAccessType !== sessionAccessType
    ) {
      return sessionAccessType;
    }

    return queryAccessType;
  });

  const userAccessMapping: Partial<
    Record<AccessTypeEnum, AccessDeniedPropType>
  > = {
    [AccessTypeEnum.No_Account_Access]: {
      title: 'Access Required',
      description: (
        <>
          You don’t have access to this account. Your organization or hiring
          organization may not have granted you access to this account. If you
          believe this is an error, reach out to your organization’s
          administrator or contact{' '}
          <Link href={`mailto:${email}`} sx={{ color: 'primary.main' }}>
            {email}
          </Link>{' '}
          for help.
        </>
      ),
      actionButtons: {
        secondary: {
          label: 'Log Out',
          onClick: () => logout(),
        },
      },
    },
    [AccessTypeEnum.Deactivated]: {
      title: 'Access Deactivated',
      description: (
        <>
          Your account access has been deactivated. If you believe this is an
          error, reach out to your organization’s administrator or contact{' '}
          <Link href={`mailto:${email}`} sx={{ color: 'primary.main' }}>
            {email}
          </Link>{' '}
          for help.
        </>
      ),
      actionButtons: {
        secondary: {
          label: 'Log Out',
          onClick: () => logout(),
        },
      },
    },
    [AccessTypeEnum.Invited]: {
      title: "You're Invited",
      description: (
        <>
          You have been invited to collaborate to{' '}
          <Typography component="span" fontWeight={600}>
            {accountDisplayName}
          </Typography>{' '}
          account
          {invitedByName && (
            <>
              {' '}
              by{' '}
              <Typography component="span" fontWeight={600}>
                {invitedByName}
              </Typography>
            </>
          )}
          . Accept the invitation to get started.
        </>
      ),
      actionButtons: {
        primary: {
          label: 'Accept',
          onClick: acceptAccountInvite,
        },
        secondary: {
          label: 'Decline',
          onClick: () => setShowConfirmationModal(true),
        },
      },
    },
  };

  // Show access denied message for users with no access to given route
  if (accessType === AccessTypeEnum.Access_Denied) {
    return (
      <AccessDenied
        title="Access Denied"
        description={
          <>
            You don’t have permission to view this page. If you believe this is
            an error, reach out to your organization’s administrator or contact{' '}
            <Link href={`mailto:${email}`} sx={{ color: 'primary.main' }}>
              {email}
            </Link>{' '}
            for help.
          </>
        }
        actionButtons={{
          primary: {
            label: 'Go Back',
            onClick: () => navigate(-1),
          },
          secondary: {
            label: 'Log Out',
            onClick: () => logout(),
          },
        }}
      />
    );
  }

  // Invited users should be redirected to home page after accepting the invitation
  if (accessType === AccessTypeEnum.Active && clients.length > 0) {
    return <Navigate to={`/${clients[0].id}/campaigns`} />;
  }

  /**
   * This should only be the case when the user is invited to an org, logs in, and then is invited
   * to an account while still logged in. If so, first view is No_Account_Access and then Invited.
   */
  if (
    !isNil(accountId) &&
    !isNil(queryAccessType) &&
    queryAccessType !== accessType
  ) {
    return <Navigate to={`/access/restricted?type=${accessType}`} />;
  }

  return (
    <>
      <AccessDenied
        title={userAccessMapping[accessType]?.title}
        description={userAccessMapping[accessType]?.description}
        {...(!!userAccessMapping[accessType]?.actionButtons && {
          actionButtons: {
            primary: {
              label: userAccessMapping[accessType].actionButtons.primary?.label,
              onClick:
                userAccessMapping[accessType].actionButtons.primary?.onClick,
            },
            secondary: {
              label:
                userAccessMapping[accessType].actionButtons.secondary?.label,
              onClick:
                userAccessMapping[accessType].actionButtons.secondary?.onClick,
            },
          },
        })}
      />

      <InkModal
        open={showConfirmationModal}
        onClose={() => setShowConfirmationModal(false)}
        loading={isDecliningInvitation}
        title="Decline Invitation"
        primaryButton={{
          onClick: declineAccountInvite,
          label: 'Decline',
        }}
        secondaryButton={{
          onClick: () => setShowConfirmationModal(false),
          label: 'Cancel',
        }}
      >
        This will decline the invitation to the {accountDisplayName} account.
        You won't have access to the associated content or be able to accept
        this invite later.
      </InkModal>
    </>
  );
};

export default AccessRequired;
